import { T, Var, useGT } from 'gt-react';
import { PaperclipIcon, XIcon } from '@phosphor-icons/react';
import { base64DecodedBytes } from '@erp/shared-utils';
import { cn } from '@/lib/utils';
import {
  formatByteSize,
  type AttachmentBudget,
  type AttachmentDocumentRef,
  type ComposeReplyAttachmentInput,
} from './composeReplyAttachments';

const CHIP_CLASS =
  'inline-flex items-center gap-1 rounded-md border bg-muted/40 pl-2 text-xs';
const REMOVE_CLASS =
  'inline-flex size-6 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground';

interface ComposeAttachmentChipsProps {
  /** Base64 files; each chip can be removed, Excel chips open a preview. */
  files: ComposeReplyAttachmentInput[];
  /** Stored documents attached by reference (forwarding); removable. */
  refs: AttachmentDocumentRef[];
  budget: AttachmentBudget;
  isPreviewable: (file: ComposeReplyAttachmentInput) => boolean;
  onPreview: (file: ComposeReplyAttachmentInput) => void;
  onRemoveFile: (index: number) => void;
  onRemoveRef: (documentId: string) => void;
}

/**
 * The attachment strip of the compose panel: one removable chip per file,
 * plus the running total against the inline send limit. Renders nothing when
 * there is nothing attached.
 */
export function ComposeAttachmentChips({
  files,
  refs,
  budget,
  isPreviewable,
  onPreview,
  onRemoveFile,
  onRemoveRef,
}: ComposeAttachmentChipsProps) {
  const gt = useGT();
  if (files.length === 0 && refs.length === 0) return null;

  const removeLabel = gt('Remove attachment', {
    context: 'aria label of the x button on an attachment chip',
  });
  const previewTitle = gt('Click to preview', {
    context: 'compose attachment chip tooltip',
  });

  return (
    <div className="border-t px-3 py-2">
      <div className="flex flex-wrap gap-2">
        {files.map((file, index) => {
          const previewable = isPreviewable(file);
          return (
            <span key={file.documentId ?? file.name} className={CHIP_CLASS}>
              <button
                type="button"
                disabled={!previewable}
                onClick={() => onPreview(file)}
                title={previewable ? previewTitle : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 py-1',
                  previewable && 'cursor-pointer hover:underline',
                )}
              >
                <PaperclipIcon className="h-3.5 w-3.5 shrink-0" />
                <span className="max-w-56 truncate">{file.name}</span>
                <span className="text-muted-foreground">
                  {formatByteSize(base64DecodedBytes(file.contentBytes))}
                </span>
              </button>
              <button
                type="button"
                aria-label={removeLabel}
                onClick={() => onRemoveFile(index)}
                className={REMOVE_CLASS}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        {refs.map((ref) => (
          <span key={ref.documentId} className={CHIP_CLASS}>
            <span className="inline-flex items-center gap-1.5 py-1">
              <PaperclipIcon className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-56 truncate">{ref.name}</span>
              <span className="text-muted-foreground">
                {formatByteSize(ref.sizeBytes)}
              </span>
            </span>
            <button
              type="button"
              aria-label={removeLabel}
              onClick={() => onRemoveRef(ref.documentId)}
              className={REMOVE_CLASS}
            >
              <XIcon className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <p
        className={cn(
          'mt-1.5 text-xs',
          budget.isOver ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        <T context="running attachment size total under the compose attachment chips, e.g. 1.2 MB of 2.9 MB">
          <Var>{formatByteSize(budget.totalBytes)}</Var> of{' '}
          <Var>{formatByteSize(budget.limitBytes)}</Var>
        </T>
        {budget.isOver && (
          <>
            {' · '}
            <T context="shown when attachments exceed the email size limit; tells the user how much to remove">
              Remove at least <Var>{formatByteSize(budget.overBy)}</Var> of
              attachments to send
            </T>
          </>
        )}
      </p>
    </div>
  );
}
